import React, { useCallback } from "react";
import { TableBody, TableCell, TableRow, Tooltip } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { confirmAlert } from "react-confirm-alert";
import clsx from "clsx";
import Button from "../DesignSystem/button";
import TitleSubtitle from "../DesignSystem/Table/Cells/TitleSubtitle";
import ElipsisCell from "../DesignSystem/Table/Cells/ElipsisTextCell";
import soldIcon from "../../assets/images/ic-sold.svg";
import EditIcon from "../../assets/images/edit-icon.svg";
import InUseIcon from "../../assets/images/in-use-icon.svg";
import WarningInfoIcon from "../../assets/images/warn-toast-icon.svg";
import { areYouSureToaster, getDateInMediumFormat, wrapInTooltip } from "../../utils/methods";
import ManageBandwidth from ".";
import AuthService from "../../services/auth.service";
import { Roles } from "../../enums";
import LinkGreenConsumed from "../../assets/images/link-green-consumed.svg";
import LinkGreenAvailable from "../../assets/images/link-green-available.svg";
import LinkGrey from "../../assets/images/link-grey-consumed.svg";
import LinkYellow from "../../assets/images/link-yellow-available.svg";
import LinkWhite from "../../assets/images/link-white-available.svg";
import LinkRed from "../../assets/images/link-red-consumed.svg";
import { PortDetails } from "../PortDetails";
import LinkStatus from "./Table/LinkStatus";

interface TableDataProps {
  bandwidthPort: any;
  removeLoadingId: string;
  cancelSubLoading: string;
  handlePutForSaleOpen: () => void;
  onSaleEdit: (port: any) => void;
  onRemoveClick: (port: any) => void;
  onCancelSubscription: (port: any) => void;
  onEditBandwidth: (port: any) => void;
  refetch: () => void;
}
const TableData: React.FC<TableDataProps> = ({
  onSaleEdit,
  onRemoveClick,
  removeLoadingId,
  bandwidthPort,
  cancelSubLoading,
  handlePutForSaleOpen,
  onCancelSubscription,
  onEditBandwidth,
  refetch,
}) => {
  const isAdmin = AuthService.getRole() === Roles.admin;
  const handleEdit = useCallback(() => {
    onSaleEdit(bandwidthPort);
  }, [onSaleEdit, bandwidthPort]);
  const handleEditBandwidth = useCallback(() => {
    onEditBandwidth(bandwidthPort);
  }, [onEditBandwidth, bandwidthPort]);
  const handleCancel = useCallback(() => {
    return areYouSureToaster(onCancelSubscription, bandwidthPort, "Are you sure to you want to cancel this subscription?");
  }, [bandwidthPort, onCancelSubscription]);

  const handleRemove = useCallback(() => {
    return areYouSureToaster(onRemoveClick, bandwidthPort, "Are you sure you want to remove this listing?");
  }, [onRemoveClick, bandwidthPort]);

  const editTooltip = (
    <>
      <br />
      <PortDetails nrId={bandwidthPort.n_port_id} otherNrId={bandwidthPort.f_port_id} isBandwidth doNotSortLeftRight refreshIndex={refetch} />
    </>
  );
  const linkName = bandwidthPort.status === "cancel" ? bandwidthPort.purchase_bandwidth_name : bandwidthPort.bandwidth_name;
  const getMarketplaceButton = () => {
    let button;
    if (bandwidthPort.status === "in_use") {
      button = <img src={InUseIcon} alt="In Use" />;
    } else if (!bandwidthPort.status) {
      button = <Button className="btn-sale" variant="success" node="Sell" onClick={handlePutForSaleOpen} />;
    } else if (bandwidthPort.status === "put_up_for_sale_bandwidth") {
      button = (
        <>
          <Button
            className="btn-remove-from-market-place"
            variant="success"
            node="Remove"
            loading={!!(removeLoadingId === bandwidthPort.bandwidth_detail_id)}
            onClick={handleRemove}
          />
          <Tooltip
            placement="right"
            title="Edit Listing"
            classes={{
              tooltip: "tooltip",
            }}
            arrow
          >
            <img className="edit-icon" src={EditIcon} alt="Edit" onClick={handleEdit} />
          </Tooltip>
        </>
      );
    } else if (bandwidthPort.status === "cancel") {
      if (bandwidthPort.price_type.includes("on_demand")) {
        button = (
          <Button
            className="btn-cancel-subscription"
            variant="success"
            onClick={handleCancel}
            loading={!!(cancelSubLoading === bandwidthPort.purchase_id)}
            node="Release"
          />
        );
      } else if (bandwidthPort.on_contract_price > 0 && bandwidthPort.days_until_contract_expire > 0) {
        const text = `Expires on ${getDateInMediumFormat(bandwidthPort.expiring_date)}`;
        button = (
          <Button
            className={clsx("btn-cancel-subscription small", !isAdmin && "disabled")}
            variant="success"
            onClick={handleCancel}
            disabled={!isAdmin}
            loading={!!(cancelSubLoading === bandwidthPort.purchase_id)}
            node={text}
          />
        );
      } else if (bandwidthPort.on_contract_price > 0) {
        button = (
          <Button
            className="btn-cancel-subscription"
            variant="success"
            onClick={handleCancel}
            loading={!!(cancelSubLoading === bandwidthPort.purchase_id)}
            node="Cancel"
          />
        );
      } else {
        button = (
          <Button
            className="btn-cancel-subscription"
            variant="success"
            onClick={handleCancel}
            disabled
            loading={!!(cancelSubLoading === bandwidthPort.purchase_id)}
            node="Unexpected"
          />
        );
      }
    } else if (bandwidthPort.status === "sold" && !bandwidthPort.purchasePortId) {
      button = wrapInTooltip(<img src={soldIcon} alt="Sold" />, `Sold to ${bandwidthPort.buyer_organization}`);
    }
    return button;
  };

  return (
    <TableRow>
      <TitleSubtitle
        width="10%"
        maxWidth="15vw"
        align="left"
        isOnNetNE={bandwidthPort.nearend_on_net}
        title={bandwidthPort.nne_name}
        subTitle={`${bandwidthPort.n_group_name} ${bandwidthPort.nearend_port_name}`}
        boldTitle
        blackBorder
      />
      <TitleSubtitle
        width="10%"
        maxWidth="15vw"
        align="left"
        isOnNetNE={bandwidthPort.farend_on_net}
        title={bandwidthPort.fne_name}
        subTitle={`${bandwidthPort.f_group_name} ${bandwidthPort.farend_port_name}`}
        boldTitle
        blackBorder
      />
      <TableCell width="20%" align="left" />
      <TitleSubtitle
        width="20%"
        maxWidth="20vw"
        align="left"
        title=""
        titleNode={
          <span style={{ display: "flex" }}>
            <span style={{ paddingRight: "5px" }}>{bandwidthPort.bandwidth_description}</span>
            {editTooltip}
          </span>
        }
        subTitle={linkName}
        boldTitle
      />
      <TableCell width="20%" align="left">
        {bandwidthPort.provider || "-"}
      </TableCell>
      <TableCell align="left" width="15%">
        <div className="group-port-manage-actions">
          <LinkStatus bandwidthPort={bandwidthPort} />
        </div>
      </TableCell>
      <TableCell align="left" width="15%">
        <Tooltip
          placement="left"
          title={
            !bandwidthPort.is_verified && bandwidthPort.status !== "in_use" ? (
              <div>
                <div>One side port is not verified.</div>
                <div>Please contact WaveXchange Administrator.</div>
              </div>
            ) : (
              ""
            )
          }
          classes={{
            tooltip: "tooltip",
          }}
          arrow
        >
          <div className="group-port-manage-actions">{getMarketplaceButton()}</div>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

export default TableData;
